import { styled } from '@mui/material/styles';
import {
  QContainer,
  QThemeProvider,
  QTabContext,
  QTabList,
  QTab,
  QTabPanel,
  QButton,
  AnalysisRequestBulkSubmitFileImport,
  AnalysisRequestBulkSubmitTextInput,
  AnalysisRequestBulkSubmitDiscardedSubmissionsDrawer,
  AnalysisRequestBulkSubmitUploadComplete,
  validateStoreLinks,
} from '@kw/quokka-ui';
import { ChangeEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useSnackbar } from 'notistack';
import BackButton from '../../shared/BackButton';
import { httpRequest } from '../../../utils/network.service';
import { isBulkSubmitStoreLinkFile, parseBulkSubmitStoreLinkFileContent } from './parseBulkSubmitStoreLinkFile';
import { APPLICATIONS_ALL_STATUSES_PATH } from './requestAnalysisNavigation';

export default function RequestAnalysisBulkSubmit() {
  function a11yProps(index: number) {
    return {
      id: `request-analysis-bulk-submit-tab-${index}`,
      'aria-controls': `request-analysis-bulk-submit-tabpanel-${index}`,
    };
  }
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [submissions, setSubmissions] = useState({ accepted: [], rejected: [] });
  const [textInputValue, setTextInputValue] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileImportWarningMessage, setFileImportWarningMessage] = useState<string>('');
  const [uploadCompletedAt, setUploadCompletedAt] = useState<Date | null>(null);
  const [showDiscardedSubmissionsDrawer, setShowDiscardedSubmissionsDrawer] = useState<boolean>(false);
  const [processingSubmissions, setProcessingSubmissions] = useState<boolean>(false);
  /* 
    numAccepted is used to track number of valid submissions after submit is clicked in order to ensure
    that the API request is only fired once all state updates are completed.
  */
  const [numAccepted, setNumAccepted] = useState<number>(0);

  const showUploadComplete = uploadCompletedAt !== null;

  const handleChange = (_event: ChangeEvent<HTMLButtonElement>, tab: number) => {
    setActiveTab(tab);
    // if switching tabs and upload complete, reset state
    if (showUploadComplete) {
      setSubmissions({ accepted: [], rejected: [] });
      setUploadCompletedAt(null);
      setShowDiscardedSubmissionsDrawer(false);
      setFileImportWarningMessage('');
      setSelectedFile(null);
      setTextInputValue('');
    }
  };

  const handleAddStoreLink = (event: ChangeEvent<HTMLInputElement>) => {
    setTextInputValue(event.target.value);
  };

  const updateSubmissions = (accepted: string[], rejected: { url: string; reason: string }[]) => {
    const uniqueRejected = Array.from(new Set([...rejected]));
    setSubmissions({
      accepted: [...submissions.accepted, ...accepted],
      rejected: Array.from(new Set([...submissions.rejected, ...uniqueRejected.map(({ url }) => url)])),
    });

    setTextInputValue('');
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        let lines: string[] = [];
        const fileContent = e.target?.result as string;
        // file size check
        if (file.size > 1024 * 1024) {
          setFileImportWarningMessage('Upload a file less than 1MB in size');
          return;
        }
        // file type check
        if (!isBulkSubmitStoreLinkFile(file)) {
          setFileImportWarningMessage('Upload a TXT or CSV file');
          return;
        }

        lines = parseBulkSubmitStoreLinkFileContent(fileContent);
        // file line check
        if (lines.length > 250) {
          setFileImportWarningMessage('Upload a file with 250 lines of text or less');
          return;
        }
        // file contents validation
        const { accepted, rejected } = validateStoreLinks(lines, submissions);

        if (accepted.length === 0) {
          setFileImportWarningMessage('Upload a file with valid store links');
          return;
        }

        updateSubmissions(accepted, rejected);
      };
      reader.readAsText(file);
      setSelectedFile(file);
    }
  };

  useEffect(() => {
    // reset state when a new file is selected prior to clicking upload
    if (!showUploadComplete) {
      setSubmissions({ accepted: [], rejected: [] });
      setFileImportWarningMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const handleSubmit = () => {
    setProcessingSubmissions(true);

    const validateAndUpdateSubmissions = () => {
      const lines = textInputValue.split('\n');
      const { accepted, rejected } = validateStoreLinks(lines, submissions);
      updateSubmissions(accepted, rejected);
      setNumAccepted(accepted.length);
    };

    if (activeTab === 0 && textInputValue.length) {
      validateAndUpdateSubmissions();
    }

    if (activeTab === 1 && !!selectedFile) {
      setNumAccepted(submissions.accepted.length);
    }
  };

  const submitStoreLinks = async () => {
    const { response, error } = await httpRequest({
      path: `${process.env.DEVICE_SERVICE_URL}/application/orgapp/bulk-submit`,
      method: 'POST',
      body: submissions.accepted,
    });

    if (error) {
      console.error('Error submitting store links:', error);
      enqueueSnackbar(`Error submitting store links: ${error}`, { variant: 'error' });
    } else if (response.status === 201) {
      setUploadCompletedAt(new Date());
      setFileImportWarningMessage('');
      setSelectedFile(null);
      setTextInputValue('');
      setProcessingSubmissions(false);
    }
  };
  /* 
    api request is fired once all state updates have completed
    to ensure that the submissions.accepted array is not empty
  */
  useEffect(() => {
    if (numAccepted === submissions.accepted.length && numAccepted > 0) {
      submitStoreLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numAccepted]);

  const uploadCompleteScreen = uploadCompletedAt ? (
    <AnalysisRequestBulkSubmitUploadComplete
      submissions={submissions}
      setShowDiscardedSubmissionsDrawer={setShowDiscardedSubmissionsDrawer}
      uploadedAt={uploadCompletedAt}
      onReturnToRequestAnalysis={() => navigate('/request-analysis')}
      onGoToApplicationsList={() => navigate(APPLICATIONS_ALL_STATUSES_PATH)}
    />
  ) : null;
  const uploadCompletePanelSx = { padding: '24px 0 0' };

  return (
    <QThemeProvider>
      <AnalysisRequestBulkSubmitDiscardedSubmissionsDrawer
        open={showDiscardedSubmissionsDrawer}
        toggleDrawer={setShowDiscardedSubmissionsDrawer}
        rejected={submissions.rejected}
      />
      <QContainer
        sx={{
          paddingTop: '16px',
          paddingBottom: '0px',
          maxWidth: '100% !important',
          display: 'flex',
          flexDirection: 'column',
          rowGap: 2,
          overflow: 'auto',
        }}
      >
        <BackButton href="/request-analysis" text="Back to Request Analysis" />
        <StyledTabWrapper>
          <QTabContext value={activeTab}>
            <QTabList tabs="true" onChange={handleChange} aria-label="Request Analysis Bulk Submit Tabs">
              <StyledQTab label="Input Text" {...a11yProps(0)} />
              <StyledQTab label="Import from File" {...a11yProps(1)} />
            </QTabList>
            {/* TextField */}
            <QTabPanel tabid="0" value={0} index={0} sx={showUploadComplete ? uploadCompletePanelSx : { padding: '40px 0px' }}>
              {uploadCompleteScreen ?? (
                <>
                  <AnalysisRequestBulkSubmitTextInput inputValue={textInputValue} handleInput={handleAddStoreLink} />
                  <QButton
                    variant="outlined"
                    color="primary"
                    sx={{ marginTop: '16px', width: '49%', height: '40px', marginRight: '1%' }}
                    onClick={() => navigate('/request-analysis')}
                  >
                    Back to Request Analysis
                  </QButton>
                  <QButton
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: '16px', width: '49%', height: '40px', marginLeft: '1%' }}
                    onClick={handleSubmit}
                    startIcon={<FileUploadOutlinedIcon />}
                    disabled={!textInputValue.trim().length}
                  >
                    Submit Store Links
                  </QButton>
                </>
              )}
            </QTabPanel>
            {/* File Upload */}
            <QTabPanel tabid="1" value={1} index={1} sx={showUploadComplete ? uploadCompletePanelSx : undefined}>
              {uploadCompleteScreen ?? (
                <AnalysisRequestBulkSubmitFileImport
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  handleFileChange={handleFileChange}
                  handleSubmit={handleSubmit}
                  warningMessages={fileImportWarningMessage}
                  processingSubmissions={processingSubmissions}
                />
              )}
            </QTabPanel>
          </QTabContext>
        </StyledTabWrapper>
      </QContainer>
    </QThemeProvider>
  );
}

const StyledQTab = styled(QTab)`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  color: #666;
  &.Mui-selected {
    color: ${props => props.theme.palette.accent.primary};
    font-weight: 800;
  }
`;

const StyledTabWrapper = styled('div')`
  margin-top: 32px;
  margin-left: 22px;
  margin-right: 22px;
`;
